import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Pickaxe, Zap, Wallet, Copy, Check, Loader2, X, QrCode, MonitorSmartphone,
  ShieldCheck, Clock, ArrowUpRight, Coins, Users, Lock, BadgeCheck, Info, UserPlus, Trophy,
  ShieldAlert, ExternalLink, Flame,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import Reveal, { Eyebrow, EASE } from "@/components/Reveal";
import { useWallet } from "@/lib/wallet";
import WalletButton from "@/components/WalletButton";
import { payMining } from "@/lib/pay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const short = (w) => `${w?.slice(0, 4)}…${w?.slice(-4)}`;
const fmt = (n, d = 2) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const B58_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

const TIER_ORDER = ["free", "tier1", "tier2"];
const TIER_NAMES = { free: "Free Miner", tier1: "Tier 1", tier2: "Tier 2" };
const TIER_DESC = {
  free: "Everyone starts here. A taste of daily USDC, on us.",
  tier1: "The first package — serious daily output at a fraction of the price.",
  tier2: "The expensive tier — maximum output with the earliest withdrawal window.",
};

function useTicker(ms = 1000) {
  const [, setT] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setT((x) => x + 1), ms);
    return () => clearInterval(iv);
  }, [ms]);
}

function hhmmss(ms) {
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((v) => String(v).padStart(2, "0")).join(":");
}

function promoCountdown(endsAt) {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  return `${Math.floor(ms / 86400000)}d ${hhmmss(ms % 86400000)}`;
}

/* ---------------- Boost modal: wallet popup or QR ---------------- */

function BoostModal({ tier, config, onClose, onUpgraded }) {
  const { wallet, address } = useWallet();
  const [mode, setMode] = useState("wallet");
  const [currency, setCurrency] = useState("USDC");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | pending | confirmed
  const [sender, setSender] = useState(address || "");
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);
  const deadRef = useRef(false);
  useEffect(() => () => { deadRef.current = true; if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const spec = config?.tiers?.[tier];
  const promo = tier === config?.promo?.tier ? config.promo : null;
  const payLamports = promo ? promo.sol_lamports : Math.round((spec?.price_sol || 0) * 1e9);
  const usdcUnits = 1_000_000; // promo price: exactly $1
  const payUri = config && spec
    ? `solana:${config.wallet}?amount=${encodeURIComponent(String(promo ? (payLamports || 0) / 1e9 : spec.price_sol))}&label=${encodeURIComponent("B&W Pay Mining")}&message=${encodeURIComponent(`${spec.name} boost`)}`
    : "";

  const finish = (msg) => {
    setPhase("confirmed");
    toast.success(msg, { description: `${spec?.name} activated — your daily USDC rate is live` });
    onUpgraded();
  };

  const pollSignature = useCallback(async (signature, attempt) => {
    if (deadRef.current) return;
    try {
      const r = await fetch(`${API}/mining/upgrade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, tier, signature }),
      });
      const d = await r.json();
      if (r.ok && d.status === "confirmed") { finish("Payment verified on Solana"); return; }
      if (r.ok && d.status === "pending" && attempt < 25) {
        setPhase("pending");
        setAttempts(attempt + 1);
        timerRef.current = setTimeout(() => pollSignature(signature, attempt + 1), 8000);
        return;
      }
      if (!r.ok) throw new Error(d.detail || "Verification failed");
      setPhase("idle");
      toast.error("Verification timed out — check again from the mining console in a minute");
    } catch (e) {
      setPhase("idle");
      toast.error(e?.message?.slice(0, 140) || "Verification failed");
    }
  }, [address, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  const payWithWallet = async () => {
    if (!wallet?.provider || !address) return;
    if (promo && currency === "SOL" && !payLamports) {
      toast.error("SOL price feed is warming up — pay in USDC (exactly $1) meanwhile");
      return;
    }
    setBusy(true);
    try {
      const signature = await payMining({
        provider: wallet.provider, payer: address, receiver: config.wallet,
        currency: promo ? currency : "SOL", lamports: payLamports, usdcUnits,
      });
      toast.info("Payment sent — verifying on-chain…");
      await pollSignature(signature, 0);
    } catch (e) {
      toast.error(e?.message?.slice(0, 140) || "Payment cancelled");
    }
    setBusy(false);
  };

  const verifyQR = async (attempt = 0) => {
    const from = sender.trim();
    if (!B58_RE.test(from)) {
      toast.error("Enter the Solana wallet address you paid from");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/mining/poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, tier, sender: from }),
      });
      const d = await r.json();
      if (r.ok && d.status === "confirmed") { finish("Payment found on-chain"); setBusy(false); return; }
      if (r.ok && d.status === "pending" && attempt < 40) {
        setPhase("pending");
        setAttempts(attempt + 1);
        timerRef.current = setTimeout(() => verifyQR(attempt + 1), 8000);
        setBusy(false);
        return;
      }
    } catch (e) {
      toast.error(e?.message?.slice(0, 140) || "Verification failed");
    }
    setBusy(false);
  };

  if (!spec) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="boost-modal" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-line bg-card2 p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sgreen">Boost your rig</p>
            <h3 className="mt-2 text-xl font-bold text-white">Upgrade to {spec.name}</h3>
          </div>
          <button onClick={onClose} data-testid="boost-close" className="rounded-full border border-line p-2 text-mut transition-colors hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {phase === "confirmed" ? (
          <div className="mt-8 text-center" data-testid="boost-confirmed">
            <BadgeCheck className="mx-auto h-12 w-12 text-sgreen" />
            <p className="mt-4 font-semibold text-white">{spec.name} is active</p>
            <p className="mt-2 text-sm text-mut">Your wallet now mines {fmt(spec.daily_usdc)} USDC every 24 hours.</p>
            <button onClick={onClose} className="cta-glow mt-6 w-full rounded-full bg-sgreen px-6 py-3 text-sm font-semibold text-black">
              Back to mining
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 rounded-xl border border-line bg-panel p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-mut">Boost price</span>
                {promo ? (
                  <span className="font-mono font-semibold text-sgreen" data-testid="boost-price-line">
                    $1 <span className="text-mut">({currency === "SOL" ? `≈ ${((payLamports || 0) / 1e9).toFixed(4)} SOL` : "1 USDC"})</span>
                  </span>
                ) : (
                  <span className="font-mono font-semibold text-white">{spec.price_sol} SOL</span>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-mut">You mine</span>
                <span className="font-mono font-semibold text-sgreen">{fmt(spec.daily_usdc)} USDC / day</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-mut">Withdrawal window</span>
                <span className="font-mono text-white">{tier === "tier2" ? "from day 20" : "at day 45"}</span>
              </div>
              {promo && (
                <div className="mt-2 flex items-center justify-between text-sm" data-testid="boost-promo-line">
                  <span className="text-mut">Resource fee</span>
                  <span className="font-mono text-sgreen">
                    <s className="text-mut">{fmt(promo.base_price_sol)} SOL</s> $1 · ends {promoCountdown(promo.ends_at)}
                  </span>
                </div>
              )}
            </div>

            {config?.founding?.remaining > 0 && (
              <p className="mt-3 rounded-lg border border-sgreen/30 bg-sgreen/10 px-3 py-2 text-center font-mono text-[11px] text-sgreen" data-testid="boost-founding-chip">
                Founding offer · first 500 upgrades get +{config.founding.bonus_days} bonus mining days — {config.founding.remaining} left
              </p>
            )}

            <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.07] p-3" data-testid="boost-warning-note">
              <p className="flex items-start gap-2 text-[11px] leading-relaxed text-amber-100/90">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Phantom may label this request "suspicious" — that's its standard protection for <b>new websites</b>, and it relaxes
                  as bnwpay.com builds history. What you're approving is simple: <b>send exactly {promo ? "$1" : `${spec.price_sol} SOL`}</b> to
                  the official B&W wallet below — no token approvals beyond the transfer, no seed phrase, never asked. Check its history on{" "}
                  <a href={`https://solscan.io/account/${config.wallet}`} target="_blank" rel="noopener noreferrer" className="underline decoration-amber-400/50 hover:text-amber-200" data-testid="boost-solscan-link">
                    Solscan <ExternalLink className="inline h-3 w-3" />
                  </a>{" "}
                  and verify the recipient matches, then approve with confidence.
                </span>
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2 rounded-full border border-line bg-panel p-1">
              <button
                onClick={() => setMode("wallet")}
                data-testid="boost-tab-wallet"
                className={`flex items-center justify-center gap-2 rounded-full py-2 text-xs font-semibold transition-colors ${mode === "wallet" ? "bg-sgreen text-black" : "text-mut hover:text-white"}`}
              >
                <MonitorSmartphone className="h-4 w-4" /> Pay with wallet
              </button>
              <button
                onClick={() => setMode("qr")}
                data-testid="boost-tab-qr"
                className={`flex items-center justify-center gap-2 rounded-full py-2 text-xs font-semibold transition-colors ${mode === "qr" ? "bg-sgreen text-black" : "text-mut hover:text-white"}`}
              >
                <QrCode className="h-4 w-4" /> Scan QR
              </button>
            </div>

            {mode === "wallet" ? (
              <div className="mt-5">
                {promo && (
                  <div className="mb-4 grid grid-cols-2 gap-2 rounded-full border border-line bg-panel p-1" data-testid="boost-currency-toggle">
                    {[
                      ["USDC", "1 USDC — exact"],
                      ["SOL", `≈ ${((payLamports || 0) / 1e9).toFixed(4)} SOL`],
                    ].map(([cur, label]) => (
                      <button
                        key={cur}
                        onClick={() => setCurrency(cur)}
                        data-testid={`boost-currency-${cur.toLowerCase()}`}
                        className={`rounded-full py-2.5 text-center transition-colors ${currency === cur ? "bg-sgreen text-black" : "text-mut hover:text-white"}`}
                      >
                        <span className="block text-xs font-semibold">{cur}</span>
                        <span className={`block text-[10px] ${currency === cur ? "text-black/70" : "text-mut"}`}>{label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs leading-relaxed text-mut">
                  Opens your connected wallet with <span className="font-mono text-white">{promo ? "$1" : `${spec.price_sol} SOL`}</span> pre-filled to
                  the official B&W wallet — just press send. {promo ? "The $1 is your GPU rig resource fee — one-time, lifetime boost." : "Payments are verified on-chain and your tier activates automatically."}
                </p>
                <button
                  onClick={payWithWallet}
                  disabled={busy || phase === "pending" || (!!promo && currency === "SOL" && !payLamports)}
                  data-testid="boost-pay-button"
                  className="cta-glow mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-4 text-base font-semibold text-black disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-5 w-5" />}
                  {busy
                    ? "Approve in wallet…"
                    : promo
                      ? currency === "USDC" ? "Send $1 in USDC" : `Send $1 (≈ ${((payLamports || 0) / 1e9).toFixed(4)} SOL)`
                      : `Send ${spec.price_sol} SOL`}
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <div className="mx-auto w-fit rounded-xl bg-white p-3" data-testid="boost-qr-code">
                  {payUri && <QRCodeSVG value={payUri} size={180} level="M" />}
                </div>
                <p className="mt-3 text-center text-xs text-mut">
                  Scan with Phantom or Solflare on your phone — <span className="font-mono text-white">{promo ? "≈ $1 in SOL" : `${spec.price_sol} SOL`}</span> pre-filled.
                </p>
                <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-mut">Paid from (wallet address)</label>
                <input
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  placeholder="Paste the wallet address you paid from"
                  data-testid="boost-qr-sender-input"
                  className="mt-2 w-full rounded-xl border border-line bg-panel px-4 py-3 font-mono text-xs text-white outline-none transition-colors focus:border-sgreen/50"
                />
                <button
                  onClick={() => verifyQR(0)}
                  disabled={busy}
                  data-testid="boost-verify-button"
                  className="cta-glow mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-4 text-base font-semibold text-black disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                  {phase === "pending" ? `Verifying… (${attempts})` : "I've paid — verify"}
                </button>
              </div>
            )}

            {phase === "pending" && (
              <div className="mt-4 rounded-xl border border-sgreen/25 bg-sgreen/[0.06] p-3" data-testid="boost-pending-note">
                <p className="text-center font-mono text-[11px] leading-relaxed text-sgreen">
                  Watching the Solana network for your payment — this page upgrades automatically once it lands. Leave it open.
                </p>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-line bg-panel p-3">
              <p className="min-w-0 truncate font-mono text-[10px] text-mut" data-testid="boost-receiver">{config.wallet}</p>
              <button
                onClick={() => { navigator.clipboard?.writeText(config.wallet); toast.success("Official wallet copied"); }}
                data-testid="boost-copy-receiver"
                className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-mut transition-colors hover:text-sgreen"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Withdraw modal ---------------- */

function WithdrawModal({ account, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const withdraw = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/mining/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: account.wallet }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not request withdrawal");
      toast.success("Withdrawal requested", { description: "The B&W team sends USDC straight to your wallet after processing." });
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.message?.slice(0, 140) || "Withdrawal failed");
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="withdraw-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-line bg-card2 p-6 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sgreen">Withdraw</p>
            <h3 className="mt-2 text-xl font-bold text-white">Claim your mined USDC</h3>
          </div>
          <button onClick={onClose} data-testid="withdraw-close" className="rounded-full border border-line p-2 text-mut transition-colors hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 rounded-xl border border-line bg-panel p-5 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">Available</p>
          <p className="mt-2 font-mono text-4xl font-bold text-sgreen" data-testid="withdraw-amount">{fmt(account.available)} <span className="text-lg text-mut">USDC</span></p>
          <p className="mt-2 text-xs text-mut">to {short(account.wallet)}</p>
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-line bg-panel p-3">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-mut" />
          <p className="text-xs leading-relaxed text-mut">
            USDC is sent on-chain to your wallet by the B&W team after processing — usually within 48 hours. Your mining continues in the meantime.
          </p>
        </div>
        <button
          onClick={withdraw}
          disabled={busy}
          data-testid="withdraw-confirm-button"
          className="cta-glow mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-4 text-base font-semibold text-black disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Coins className="h-5 w-5" />}
          Request {fmt(account.available)} USDC
        </button>
      </div>
    </div>
  );
}

/* ---------------- Mining console ---------------- */

function Console({ config, account, onBoost, onWithdraw, onRefresh, demo }) {
  useTicker();
  const [copiedRef, setCopiedRef] = useState(false);
  const [sharePost, setSharePost] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const now = Date.now();
  const nextIn = account.next_accrual_at ? new Date(account.next_accrual_at).getTime() - now : 0;
  const progress = Math.min(100, ((account.day || 0) / account.mining_days) * 100);
  const unlocked = account.unlock_at && now >= new Date(account.unlock_at).getTime();
  const guarded = (fn) => () => (demo ? toast.info("Demo preview — connect your wallet for the real thing") : fn());

  return (
    <div className="rounded-2xl border border-line bg-card2 p-6 sm:p-8" data-testid="mining-console">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sgreen/10">
            <Pickaxe className="h-5 w-5 text-sgreen" />
          </span>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">Your virtual wallet</p>
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              {account.tier_name}
              <span className="rounded-full border border-sgreen/40 bg-sgreen/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-sgreen">
                {fmt(account.rate)} USDC / day
              </span>
              {demo && <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300">Demo view</span>}
            </p>
          </div>
        </div>
        <p className="flex items-center gap-2 font-mono text-xs text-mut" data-testid="mining-next-accrual">
          <Clock className="h-3.5 w-3.5" />
          {account.day < account.mining_days && nextIn > 0 ? `Next accrual in ${hhmmss(nextIn)}` : "Mining window complete"}
        </p>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-mut">Mined balance</p>
          <p className="mt-3 font-mono text-6xl font-bold tracking-tight text-white sm:text-7xl" data-testid="mining-balance">
            {fmt(account.accrued)}<span className="text-2xl text-sgreen sm:text-3xl"> USDC</span>
          </p>
          <p className="mt-2 font-mono text-xs text-mut">
            Day {Math.min(account.day + (nextIn > 0 ? 0 : 1), account.mining_days)} of {account.mining_days} · withdrawn {fmt(account.withdrawn)} USDC
          </p>
        </div>
        {account.tier !== "tier2" && config?.tiers && (() => {
          const nextTier = config.tiers[account.tier === "free" ? "tier1" : "tier2"];
          const promo = config?.promo;
          const gain = nextTier.daily_usdc * account.mining_days - account.rate * account.mining_days;
          return (
            <div className="relative overflow-hidden rounded-2xl border border-sgreen/50 bg-sgreen/[0.08] p-5 lg:w-[300px]" data-testid="mining-balance-boost-card">
              <div className="animate-glow pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-sgreen/25 blur-[50px]" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sgreen">Boost to {nextTier.name}</p>
              <p className="mt-2 font-mono text-3xl font-bold text-white">
                {fmt(nextTier.daily_usdc)}<span className="text-sm font-medium text-sgreen"> USDC/day</span>
              </p>
              <p className="mt-1 font-mono text-[11px] text-mut">
                +{fmt(gain)} USDC over the window · {promo ? "$1 resource fee" : `${nextTier.price_sol} SOL`}
              </p>
              <button
                onClick={guarded(() => onBoost(account.tier === "free" ? "tier1" : "tier2"))}
                data-testid="mining-boost-balance"
                className="cta-glow mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-5 py-3 text-sm font-semibold text-black"
              >
                <Zap className="h-4 w-4" />
                Boost now — {promo ? "$1" : `${nextTier.price_sol} SOL`}
              </button>
            </div>
          );
        })()}
      </div>

      <div className="mt-6">
        <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
          <motion.div className="h-full rounded-full bg-sgreen" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 1, ease: EASE }} data-testid="mining-progress-bar" />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[
          ["Daily rate", `${fmt(account.rate)} USDC`],
          ["Withdrawal unlocks", fmtDate(account.unlock_at)],
          ["Window ends", fmtDate(account.ends_at)],
        ].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-line bg-panel p-4">
            <p className="font-mono text-sm font-semibold text-white">{v}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{k}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        {account.withdrawal_status === "requested" ? (
          <div className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-sgreen/40 bg-sgreen/10 px-6 py-3.5 text-sm font-semibold text-sgreen" data-testid="mining-withdrawal-pending">
            <ShieldCheck className="h-4 w-4" /> Withdrawal requested — processing
          </div>
        ) : (
          <button
            onClick={guarded(onWithdraw)}
            disabled={!account.can_withdraw}
            data-testid="mining-withdraw-button"
            className="cta-glow inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-3.5 text-sm font-semibold text-black disabled:cursor-not-allowed disabled:opacity-30 sm:w-auto"
          >
            <Coins className="h-4 w-4" />
            {account.can_withdraw ? `Withdraw ${fmt(account.available)} USDC` : unlocked ? "Nothing available yet" : "Locked until withdrawal day"}
          </button>
        )}
        {account.tier !== "tier2" && (
          <button
            onClick={guarded(() => onBoost(account.tier === "free" ? "tier1" : "tier2"))}
            data-testid="mining-boost-console"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-sgreen/50 bg-sgreen/10 px-6 py-3.5 text-sm font-semibold text-sgreen transition-colors hover:bg-sgreen/20 sm:w-auto"
          >
            <Zap className="h-4 w-4" /> Boost to {account.tier === "free" ? "Tier 1" : "Tier 2"}
          </button>
        )}
      </div>
      {account.tier !== "tier2" && (
        <p className="mt-3 text-xs text-mut">
          {account.tier === "free"
            ? "Boost to Tier 1 (1 SOL → 5 USDC/day) or Tier 2 (10 SOL → 69 USDC/day) — your wallet popup opens with the amount pre-filled."
            : "Boost to Tier 2 (10 SOL → 69 USDC/day) and unlock withdrawal from day 20."}
        </p>
      )}

      <div className="mt-6 rounded-xl border border-line bg-panel p-4" data-testid="mining-referral-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              <UserPlus className="h-4 w-4 shrink-0 text-sgreen" /> Invite miners, earn bonus days
            </p>
            <p className="mt-1 text-xs leading-relaxed text-mut">
              Each friend that starts a rig through your link adds +{config?.referral_bonus_days ?? 1} bonus mining day to your window.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="rounded-full border border-sgreen/30 bg-sgreen/10 px-3 py-1.5 font-mono text-[11px] text-sgreen" data-testid="mining-referral-stats">
              {account.referral_count || 0} friend{(account.referral_count || 0) === 1 ? "" : "s"} mining
              {account.bonus_days > 0 ? ` · +${account.bonus_days} day${account.bonus_days === 1 ? "" : "s"}` : ""}
            </span>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(account.referral_link);
                setCopiedRef(true);
                toast.success("Referral link copied");
                setTimeout(() => setCopiedRef(false), 1500);
              }}
              data-testid="mining-referral-copy"
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 font-mono text-[11px] text-white transition-colors hover:border-sgreen/50"
            >
              {copiedRef ? <Check className="h-3.5 w-3.5 text-sgreen" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedRef ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
        <p className="mt-3 min-w-0 truncate rounded-lg bg-card2 px-3 py-2 font-mono text-[10px] text-mut" data-testid="mining-referral-link">
          {account.referral_link}
        </p>
      </div>

      <div className="mt-4 rounded-xl border border-line bg-panel p-4" data-testid="mining-share-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-white">
              <Flame className="h-4 w-4 shrink-0 text-sgreen" /> Share your streak, earn +1 bonus day
            </p>
            <p className="mt-1 text-xs leading-relaxed text-mut">
              Post your mining streak on X — each verified post adds +1 bonus mining day to your window.
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-sgreen/30 bg-sgreen/10 px-3 py-1.5 font-mono text-[11px] text-sgreen" data-testid="mining-share-stats">
            {account.share_bonus_days || 0}/{account.share_cap || 7} claimed · {account.share_count || 0} post{(account.share_count || 0) === 1 ? "" : "s"}
          </span>
        </div>
        {(() => {
          const sharesLeft = (account.share_bonus_days || 0) < (account.share_cap || 7);
          const cooldownOver = !account.next_share_at || now >= new Date(account.next_share_at).getTime();
          const shareText = `Day ${account.day} mining real USDC with @bnwpay 🔨 ${fmt(account.accrued)} USDC banked so far. Mine real USDC daily — free: ${account.referral_link}`;
          if (!sharesLeft) {
            return (
              <p className="mt-3 rounded-lg border border-line bg-card2 px-3 py-2 font-mono text-[11px] text-mut" data-testid="mining-share-cap-note">
                Streak shares earned all their bonus days — invite friends above for more.
              </p>
            );
          }
          if (!cooldownOver) {
            return (
              <p className="mt-3 rounded-lg border border-line bg-card2 px-3 py-2 font-mono text-[11px] text-mut" data-testid="mining-share-cooldown">
                Next streak share unlocks in <span className="text-sgreen">{hhmmss(new Date(account.next_share_at).getTime() - now)}</span> — keep the rig running.
              </p>
            );
          }
          const claim = async () => {
            setShareBusy(true);
            try {
              const r = await fetch(`${API}/mining/share`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wallet: account.wallet, post_link: sharePost }),
              });
              const d = await r.json();
              if (!r.ok) throw new Error(d.detail || "Could not claim");
              toast.success("+1 bonus mining day", { description: "Streak shared — your rig mines an extra day." });
              setSharePost("");
              onRefresh();
            } catch (e) {
              toast.error(e?.message?.slice(0, 140) || "Could not claim");
            }
            setShareBusy(false);
          };
          return (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mining-share-compose"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 font-mono text-xs font-semibold text-black transition-colors hover:bg-white/85"
              >
                <X className="h-3.5 w-3.5" /> Compose streak post
              </a>
              <input
                value={sharePost}
                onChange={(e) => setSharePost(e.target.value)}
                placeholder="Paste your post link (x.com/…)"
                data-testid="mining-share-link-input"
                className="min-w-0 flex-1 rounded-full border border-line bg-card2 px-4 py-2.5 font-mono text-xs text-white placeholder:text-mut outline-none transition-colors focus:border-sgreen/50"
              />
              <button
                onClick={claim}
                disabled={shareBusy}
                data-testid="mining-share-claim"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sgreen px-4 py-2.5 font-mono text-xs font-semibold text-black transition-opacity disabled:opacity-50"
              >
                {shareBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Flame className="h-3.5 w-3.5" />}
                Claim +1 day
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function Mining() {
  const { address } = useWallet();
  useTicker();
  const [config, setConfig] = useState(null);
  const [account, setAccount] = useState(null);
  const [live, setLive] = useState(null);
  const [boostTier, setBoostTier] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ref] = useState(() => new URLSearchParams(window.location.search).get("ref") || "");

  const loadConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API}/mining/config`);
      if (r.ok) setConfig(await r.json());
    } catch (e) {}
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const r = await fetch(`${API}/mining/live`);
      if (r.ok) setLive(await r.json());
    } catch (e) {}
  }, []);

  const loadAccount = useCallback(async (wallet) => {
    if (!wallet) return;
    try {
      const r = await fetch(`${API}/mining/status?wallet=${wallet}`);
      if (r.ok) setAccount(await r.json());
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadConfig();
    loadLive();
    const iv = setInterval(loadLive, 10000);
    return () => clearInterval(iv);
  }, [loadConfig, loadLive]);

  useEffect(() => {
    if (address) loadAccount(address);
    else setAccount(null);
  }, [address, loadAccount]);

  const start = async () => {
    if (!address) return;
    setStarting(true);
    try {
      const r = await fetch(`${API}/mining/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, ref }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not start mining");
      toast.success("Mining started", { description: ref ? "Rig online — your referrer earns a bonus mining day." : "Your first 0.1 USDC lands within 24 hours — welcome aboard." });
      await loadAccount(address);
      loadLive();
    } catch (e) {
      toast.error(e?.message?.slice(0, 140) || "Could not start mining");
    }
    setStarting(false);
  };

  const onUpgraded = () => {
    loadAccount(address);
    loadLive();
    loadConfig();
  };

  const tiers = config?.tiers || {};
  const demoMode = new URLSearchParams(window.location.search).get("demo") === "1";
  const demoAccount = {
    exists: true,
    wallet: "DemoMiner1111111111111111111111111111111",
    tier: "free", tier_name: "Free Miner", rate: 0.1,
    accrued: 1.7, withdrawn: 0, available: 1.7,
    day: 17, mining_days: 45, base_days: 45, bonus_days: 4,
    ends_at: new Date(Date.now() + 28 * 864e5).toISOString(),
    next_accrual_at: new Date(Date.now() + 5 * 3600e3).toISOString(),
    unlock_at: new Date(Date.now() + 28 * 864e5).toISOString(),
    can_withdraw: false, withdrawal_status: "none",
    referral_count: 6, referral_link: `${window.location.origin}/mining?ref=demo`,
    share_count: 3, share_bonus_days: 4, share_cap: 7, next_share_at: null,
  };
  const view = demoMode ? demoAccount : account;
  const mining = demoMode || !!view?.exists;

  return (
    <main className="relative min-h-screen overflow-hidden pt-[72px]" data-testid="mining-page">
      <div className="bg-grid absolute inset-0" />
      <div className="animate-glow pointer-events-none absolute -top-40 left-[-10%] h-[560px] w-[560px] rounded-full bg-sgreen/15 blur-[140px]" />

      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-12">
        {config?.promo && new Date(config.promo.ends_at).getTime() > Date.now() && new Date(config.promo.ends_at).getTime() - Date.now() < 24 * 3600 * 1000 && (
          <div className="mb-10 flex flex-col items-center gap-2 rounded-2xl border border-amber-400/40 bg-amber-400/[0.08] px-5 py-4 text-center" data-testid="promo-final-hours">
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-amber-300">
              <Flame className="h-3.5 w-3.5" /> Final hours
            </p>
            <p className="text-sm leading-relaxed text-white">
              Tier 1 drops back to 1 SOL in <b className="font-mono text-amber-300">{promoCountdown(config.promo.ends_at)}</b> — last chance to boost at 0.1 SOL.
            </p>
          </div>
        )}
        <Reveal className="text-center">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-sgreen/40 bg-sgreen/10 px-4 py-1.5" data-testid="mining-badge">
            <Pickaxe className="h-3.5 w-3.5 text-sgreen" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-sgreen">USDC Mining · live now</span>
          </div>
          <h1 className="mx-auto mt-7 max-w-3xl text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Mine <span className="text-sgreen">real USDC</span> every 24 hours.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-mut sm:text-lg">
            Connect your wallet and start free — 0.1 USDC lands in your virtual wallet every day. Boost to a paid tier
            to mine up to 69 USDC a day, then withdraw when your window unlocks.
          </p>
          <div className="mx-auto mt-8 grid max-w-2xl grid-cols-3 gap-3" data-testid="mining-live-stats">
            {[
              ["Miners", live ? live.miners.toLocaleString() : "—", "mining-stat-miners"],
              ["USDC mined", live ? fmt(live.total_mined) : "—", "mining-stat-mined"],
              ["Free seats left", live ? (live.tiers?.free?.remaining ?? 0).toLocaleString() : "—", "mining-stat-seats"],
            ].map(([k, v, tid]) => (
              <div key={k} className="rounded-xl border border-line bg-card2 p-4" data-testid={tid}>
                <p className="font-mono text-lg font-semibold text-white sm:text-2xl">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{k}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {config?.promo && (
          <Reveal delay={0.04}>
            <div className="mt-14 grid items-center gap-8 md:grid-cols-2" data-testid="promo-poster-section">
              <img
                src="/mining-boost-poster.jpg"
                alt="$1 Boost — spend $1, mine up to 5 USDC daily with B&W Pay"
                className="mx-auto w-full max-w-[340px] rounded-2xl border border-line shadow-[0_0_60px_rgba(20,241,149,0.15)]"
                data-testid="promo-poster"
              />
              <div>
                <Eyebrow>Limited mining bounty · 48h</Eyebrow>
                <h2 className="mt-3 text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">
                  The <span className="text-sgreen">$1 Boost</span> — spend $1, mine 5 USDC every day.
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-mut sm:text-base">
                  The $1 is a <span className="text-white">resource fee</span> — it covers the GPU rig slot's compute and energy
                  share for 45 days. It's not a subscription; pay once and your rig is boosted for its lifetime.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm text-white/85">
                  <li className="flex items-center gap-2"><Zap className="h-4 w-4 shrink-0 text-sgreen" /> 0.1 USDC/day → 5 USDC/day instantly</li>
                  <li className="flex items-center gap-2"><Coins className="h-4 w-4 shrink-0 text-sgreen" /> $225 total over the 45-day window — 225× your free rig</li>
                  <li className="flex items-center gap-2"><Wallet className="h-4 w-4 shrink-0 text-sgreen" /> Payable in USDC (exactly $1) or SOL at the live rate</li>
                  <li className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0 text-sgreen" /> After 48h the resource fee returns to 1 SOL — permanently</li>
                </ul>
                <p className="mt-5 font-mono text-xs text-sgreen" data-testid="promo-poster-countdown">
                  Ends in {promoCountdown(config.promo.ends_at)}
                </p>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.08}>
          <div className="mt-14">
            {demoMode ? (
              <Console config={config} account={view} demo onBoost={setBoostTier} onWithdraw={() => setWithdrawOpen(true)} onRefresh={() => {}} />
            ) : !address ? (
              <div className="rounded-2xl border border-line bg-card2 p-8 text-center" data-testid="mining-connect-gate">
                <Wallet className="mx-auto h-10 w-10 text-sgreen" />
                <p className="mt-4 font-semibold text-white">Connect a Solana wallet to start mining</p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-mut">Phantom and Solflare supported. Free tier — no subscription, no seed phrases, ever.</p>
                <div className="mt-6 flex justify-center">
                  <WalletButton variant="solid" />
                </div>
              </div>
            ) : mining ? (
              <Console config={config} account={view} demo={demoMode} onBoost={setBoostTier} onWithdraw={() => setWithdrawOpen(true)} onRefresh={() => loadAccount(address)} />
            ) : (
              <div className="rounded-2xl border border-line bg-card2 p-8 text-center" data-testid="mining-start-card">
                <Pickaxe className="mx-auto h-10 w-10 text-sgreen" />
                <p className="mt-4 font-semibold text-white">Ready to mine, {short(address)}</p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-mut">
                  Start the free rig now — your virtual wallet accrues 0.1 USDC every 24 hours for 45 days.
                </p>
                <button
                  onClick={start}
                  disabled={starting}
                  data-testid="mining-start-button"
                  className="cta-glow mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-sgreen px-8 py-4 text-base font-semibold text-black disabled:opacity-40"
                >
                  {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Pickaxe className="h-5 w-5" />}
                  Start Mining — Free
                </button>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="mt-20" data-testid="mining-tiers">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <Eyebrow>Pick your rig</Eyebrow>
                <h2 className="mt-3 text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">Mining tiers</h2>
              </div>
              <p className="flex items-center gap-2 font-mono text-xs text-mut">
                <Users className="h-3.5 w-3.5" /> Limited seats per tier — subscriptions go to the official B&W wallet
              </p>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {TIER_ORDER.map((t, i) => {
                const spec = tiers[t];
                if (!spec) return null;
                const current = mining && view.tier === t;
                const pct = spec.cap ? (spec.taken / spec.cap) * 100 : 0;
                const isBoost = t !== "free" && mining && TIER_ORDER.indexOf(view.tier) < TIER_ORDER.indexOf(t);
                return (
                  <div
                    key={t}
                    className={`flex h-full flex-col rounded-2xl border p-6 ${t === "tier2" ? "border-sgreen/40 bg-card2" : "border-line bg-card2"}`}
                    data-testid={`mining-tier-card-${t}`}
                  >
                    {t === "tier2" && (
                      <span className="mb-3 w-fit rounded-full bg-sgreen px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-black">
                        Max output
                      </span>
                    )}
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut">{spec.name}</p>
                    <p className="mt-3 font-mono text-4xl font-bold text-white">
                      {t === "tier1" && config?.promo?.tier === "tier1" ? (
                        <span data-testid="tier1-price">
                          $1<span className="text-base font-medium text-sgreen"> / lifetime boost</span>
                          <span className="block font-mono text-xs font-medium text-mut">≈ {spec.price_sol ? spec.price_sol.toFixed(4) : "…"} SOL · was {fmt(config.promo.base_price_sol)} SOL</span>
                        </span>
                      ) : (
                        <>
                          {fmt(spec.daily_usdc)}<span className="text-base font-medium text-sgreen"> USDC/day</span>
                        </>
                      )}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-mut">{TIER_DESC[t]}</p>
                    <ul className="mt-5 space-y-2.5 text-sm text-white/85">
                      <li className="flex items-center gap-2">
                        {t === "free" ? <Check className="h-4 w-4 shrink-0 text-sgreen" /> : <Zap className="h-4 w-4 shrink-0 text-sgreen" />}
                        {t === "free"
                          ? "No subscription — free forever rig"
                          : t === "tier1" && config?.promo?.tier === "tier1" ? (
                              <span className="min-w-0">
                                <b className="text-sgreen">$1 one-time resource fee</b> — your GPU rig slot, lifetime boost
                              </span>
                            ) : (
                              `${spec.price_sol} SOL one-time subscription`
                            )}
                      </li>
                      <li className="flex items-center gap-2">
                        <Clock className="h-4 w-4 shrink-0 text-sgreen" />
                        Mines for 45 days
                      </li>
                      <li className="flex items-center gap-2">
                        <Lock className="h-4 w-4 shrink-0 text-sgreen" />
                        {t === "tier2" ? "Withdraw from day 20" : "Withdrawal at day 45"}
                      </li>
                    </ul>
                    <div className="mt-auto pt-6">
                      {t !== "free" && config?.promo?.tier === t && (
                        <p className="mb-3 rounded-lg border border-sgreen/40 bg-sgreen/10 px-3 py-2 text-center font-mono text-[11px] text-sgreen" data-testid={`${t}-promo-countdown`}>
                          $1 resource fee ends in {promoCountdown(config.promo.ends_at)} — then {fmt(config.promo.base_price_sol)} SOL
                        </p>
                      )}
                      {t !== "free" && config?.founding?.remaining > 0 && (
                        <p className="mb-3 rounded-lg border border-sgreen/25 bg-sgreen/[0.07] px-3 py-2 font-mono text-[10px] leading-relaxed text-sgreen" data-testid={`mining-tier-founding-${t}`}>
                          Founding offer — first 500 upgrades get +{config.founding.bonus_days} bonus mining days ({config.founding.remaining} left)
                        </p>
                      )}
                      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-mut">
                        <span>{spec.taken.toLocaleString()} / {spec.cap.toLocaleString()} seats</span>
                        <span>{spec.remaining.toLocaleString()} left</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                        <div className="h-full rounded-full bg-sgreen" style={{ width: `${pct}%` }} data-testid={`mining-tier-seats-${t}`} />
                      </div>
                      {!address ? (
                        <div className="mt-5">
                          <WalletButton variant="solid" />
                        </div>
                      ) : current ? (
                        <div className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-sgreen/40 bg-sgreen/10 px-5 py-3 text-sm font-semibold text-sgreen">
                          <BadgeCheck className="h-4 w-4" /> Current tier
                        </div>
                      ) : t === "free" ? (
                        mining ? (
                          <div className="mt-5 inline-flex w-full items-center justify-center rounded-full border border-line px-5 py-3 font-mono text-xs text-mut">
                            Passed — you're on {view.tier_name}
                          </div>
                        ) : (
                          <button
                            onClick={start}
                            disabled={starting || spec.remaining <= 0}
                            data-testid="mining-tier-start-free"
                            className="cta-glow mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-5 py-3 text-sm font-semibold text-black disabled:opacity-40"
                          >
                            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pickaxe className="h-4 w-4" />}
                            {spec.remaining > 0 ? "Start Mining — Free" : "Free tier full"}
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => setBoostTier(t)}
                          disabled={spec.remaining <= 0}
                          data-testid={`mining-boost-${t}`}
                          className="cta-glow mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-5 py-3 text-sm font-semibold text-black disabled:opacity-40"
                        >
                          {spec.remaining > 0 ? <Zap className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                          {spec.remaining > 0 ? (t === "tier1" && config?.promo?.tier === "tier1" ? "Boost — $1" : `Boost — ${spec.price_sol} SOL`) : "Tier full"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          {!!live?.leaderboard?.length && (
            <div className="mt-20" data-testid="mining-leaderboard">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <Eyebrow>Top rigs</Eyebrow>
                  <h2 className="mt-3 text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">Miner leaderboard</h2>
                </div>
                <p className="flex items-center gap-2 font-mono text-xs text-mut">
                  <Trophy className="h-3.5 w-3.5 text-sgreen" /> Live · top {live.leaderboard.length} by USDC mined
                </p>
              </div>
              <div className="mt-8 overflow-hidden rounded-2xl border border-line bg-card2">
                <div className="divide-y divide-[#1a1a1a]">
                  {live.leaderboard.map((m, i) => (
                    <div key={m.wallet} className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6" data-testid={`mining-leaderboard-row-${i}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold ${
                            i === 0 ? "bg-sgreen text-black" : i < 3 ? "border border-sgreen/50 text-sgreen" : "border border-line text-mut"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-white">{short(m.wallet)}</p>
                          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mut">
                            {TIER_NAMES[m.tier] || "Free Miner"} · day {m.day}
                            {m.bonus_days > 0 ? ` · +${m.bonus_days} bonus` : ""}
                          </p>
                        </div>
                      </div>
                      <p className="shrink-0 font-mono text-sm font-semibold text-sgreen sm:text-base" data-testid={`mining-leaderboard-usdc-${i}`}>
                        {fmt(m.accrued)} USDC
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-20" data-testid="mining-how">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-3 text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">From connect to cash-out</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {[
                { icon: Wallet, t: "Connect & start", d: "Link Phantom or Solflare and switch your free rig on. Your virtual wallet starts accruing 0.1 USDC a day." },
                { icon: Zap, t: "Boost anytime", d: "Pay the tier subscription from your wallet popup or the QR code — the network confirms it and your daily rate jumps instantly." },
                { icon: Coins, t: "Withdraw", d: "When your window unlocks (day 20 on Tier 2, day 45 otherwise), request your USDC and it's sent on-chain to your wallet." },
              ].map((s, i) => (
                <div key={s.t} className="h-full rounded-2xl border border-line bg-card2 p-6" data-testid={`mining-step-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sgreen/10">
                      <s.icon className="h-5 w-5 text-sgreen" />
                    </span>
                    <span className="font-mono text-2xl font-bold text-white/10">0{i + 1}</span>
                  </div>
                  <p className="mt-4 font-semibold text-white">{s.t}</p>
                  <p className="mt-2 text-sm leading-relaxed text-mut">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 flex flex-col items-start gap-4 rounded-2xl border border-line bg-card2 p-6 sm:flex-row sm:items-center" data-testid="mining-note">
            <ShieldCheck className="h-8 w-8 shrink-0 text-sgreen" />
            <p className="text-sm leading-relaxed text-mut">
              <span className="font-semibold text-white">How payouts work.</span> Every subscription payment goes on-chain
              to the official B&W wallet (<span className="font-mono text-white/80">{config ? short(config.wallet) : "…"}</span>) and is verified
              on the Solana network before your tier activates. USDC withdrawals are sent from the B&W treasury after your
              unlock date — mining rewards accrue in your virtual wallet here, and seats per tier are strictly limited.
            </p>
          </div>
        </Reveal>
      </div>

      {boostTier && config && (
        <BoostModal tier={boostTier} config={config} onClose={() => setBoostTier(null)} onUpgraded={onUpgraded} />
      )}
      {withdrawOpen && mining && (
        <WithdrawModal account={view} onClose={() => setWithdrawOpen(false)} onDone={() => loadAccount(address)} />
      )}
    </main>
  );
}